import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Tooltip,
  CircularProgress,
  Menu,
} from '@mui/material';
import MyLocationOutlinedIcon from '@mui/icons-material/MyLocationOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ActionMenuItem, { ACTION_MENU_LIST_PROPS, ACTION_MENU_PAPER_PROPS } from '../ActionMenuItem';
import { colors } from '../../../styles/theme';
import { getRecordCardLocateActiveSx } from '../UserLotListsPanel/UserLotListsPanel.styles';
import { getDisplayUnits } from '../../utils/userLotUnits';
import { hasSharedLotUserContentDisplayData } from '../../utils/lotUserContentFormUtils';
import RecordLotIdentity, { RecordExpandButton, RecordLotContentIdentity, RecordUnitIdentity, recordCardHeaderIconSx } from '../RecordLotIdentity';
import { hasCommercialSignal } from '../../../Leads/commercialSummary';
import { unitLocationDistinctFromLot } from '../../utils/recordLocation';
import { getLotContentSummary } from '../../utils/lotContentSummary';
import {
  formatCollectionInquiryItemCountLabel,
  getCollectionInquiryItemCounts,
} from '../../utils/collectionInquiryStats';

const BG_ELEVATED = '#242424';
const BG_UNITS = '#141414';
const TEXT_SECONDARY = '#ABABAB';
const BORDER = '#383838';
const CARD_PAD_X = 12;
const HEADER_GAP = 8;

export default function ListDetailLotCard({
  lot,
  isExpanded,
  onToggleExpand,
  onOpenLot,
  onOpenListUnit,
  onOpenSharedUnit,
  onOpenSharedLot,
  onOpenListLot,
  onLocateLot,
  onHoverLot,
  onRemoveLot,
  onRemoveUnit,
  updatingUnitId,
  readOnly = false,
  activeSharedUserLotId = null,
  activeSharedLotId = null,
  /** Acento opcional (vistas compartidas branded); default Terrio. */
  accentColor = null,
  inquiryByItem = null,
  /** Marcadores son personales: en espacio ajeno no se pinta el del owner. */
  showFavorite = true,
  /**
   * Lote ubicado desde ⋮ (panel Listas): card marcada como en Registros.
   * La vista `/shared/:token` sigue marcando vía `activeSharedLotId`.
   */
  locateActive = false,
  /** Hover del lote en el mapa: borde del acento, sin pisar la card ubicada. */
  isMapHovered = false,
}) {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const cardAccent = accentColor || colors.primary;
  const units = getDisplayUnits(lot);
  const showInquiryMeta = inquiryByItem != null;
  const inquiryTotal = showInquiryMeta
    ? (getCollectionInquiryItemCounts(
      inquiryByItem,
      lot?.lot_id,
      lot?.id,
      ...units.map((unit) => unit?.id || unit?.user_lot_id),
    )?.total || 0)
    : 0;
  const unitCount = units.length;
  /** Con 1+ unidades funcionales se puede desplegar el detalle de cada una. */
  const hasUnits = unitCount > 0;
  const showCommercial = hasCommercialSignal(lot?.commercial_rollup);
  const isFavorite = showFavorite && lot?.is_favorite === true;
  const singleUnit = unitCount === 1 ? units[0] : unitCount === 0 ? lot : null;
  const isDirectSharedLot = readOnly && !hasUnits;
  const sharedLotUserContent = lot?.lot_user_content ?? null;
  const showSharedLotContent = readOnly && hasSharedLotUserContentDisplayData(sharedLotUserContent);
  const isDirectSharedLotActive = isDirectSharedLot
    && activeSharedLotId != null
    && String(lot?.lot_id) === String(activeSharedLotId);
  const isSharedLotViewActive = readOnly
    && hasUnits
    && activeSharedLotId != null
    && String(lot?.lot_id) === String(activeSharedLotId);
  const isListLotViewActive = hasUnits
    && activeSharedLotId != null
    && String(lot?.lot_id) === String(activeSharedLotId);
  const canRemoveFromList = !readOnly && (onRemoveLot || (onRemoveUnit && singleUnit));
  const isRemovingFromList =
    updatingUnitId === `lot:${lot.lot_id}` ||
    (singleUnit && updatingUnitId === singleUnit.id) ||
    units.some((unit) => updatingUnitId === unit.id);
  const hasOwnLotContent = !readOnly && (lot?.has_lot_content === true || lot?.item_type === 'lot_content');
  const showContentChip = hasOwnLotContent || showSharedLotContent;
  // Propia: `lot_content` (include=lot_content_summary). Compartida: se arma de `lot_user_content`.
  const lotContent = showContentChip ? getLotContentSummary(lot) : null;
  /** Se despliega con UF o con contenido del lote (fila propia, primero). */
  const canExpand = hasUnits || Boolean(lotContent);
  const openLotContent = readOnly ? onOpenSharedLot : onOpenListLot;
  const contentPadLeft = CARD_PAD_X;

  const openSharedUnitDetail = (unit) => {
    if (!readOnly || !onOpenSharedUnit) return;
    onOpenSharedUnit(lot, unit || null);
  };

  const handleCardClick = () => {
    if (hasUnits) {
      if (unitCount > 1) {
        if (readOnly && onOpenSharedLot) {
          onOpenSharedLot(lot);
          return;
        }
        if (onOpenListLot) {
          onOpenListLot(lot);
          return;
        }
      }
      if (readOnly && onOpenSharedUnit) {
        openSharedUnitDetail(units[0] || null);
        return;
      }
      if (onOpenListUnit) {
        onOpenListUnit(lot, units[0] || null);
        return;
      }
      onToggleExpand?.({ stopPropagation: () => {} }, lot.lot_id);
      return;
    }

    if (readOnly && onOpenSharedUnit) {
      openSharedUnitDetail(null);
      return;
    }
    if (onOpenListUnit && singleUnit) {
      onOpenListUnit(lot, singleUnit);
      return;
    }
    onOpenLot?.(lot.lot_id);
  };

  const isSharedUnitActive = (unit) =>
    activeSharedUserLotId != null &&
    String(unit?.id || unit?.user_lot_id) === String(activeSharedUserLotId);

  const openMenu = (e) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
  };

  const closeMenu = () => setMenuAnchor(null);

  const handleRemoveFromList = () => {
    closeMenu();
    if (onRemoveLot) {
      onRemoveLot(lot);
      return;
    }
    if (onRemoveUnit && singleUnit) {
      onRemoveUnit(singleUnit);
    }
  };

  const isLocateActive = locateActive || (readOnly
    && activeSharedLotId != null
    && String(lot?.lot_id) === String(activeSharedLotId));

  return (
    <Box
      key={lot.lot_id}
      onClick={handleCardClick}
      onMouseEnter={() => onHoverLot?.(lot.lot_id)}
      onMouseLeave={() => onHoverLot?.(null)}
      data-testid="list-detail-lot-card"
      data-list-lot-id={lot?.lot_id != null ? String(lot.lot_id) : undefined}
      data-located={locateActive ? 'true' : undefined}
      sx={{
        borderRadius: '8px',
        border: 'none',
        backgroundColor: BG_ELEVATED,
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'background-color 0.15s ease',
        '&:hover': { bgcolor: '#2a2a2a' },
        ...((readOnly && onOpenSharedUnit) || onOpenListUnit ? { cursor: 'pointer' } : {}),
        ...(readOnly &&
        (isSharedLotViewActive
          || (activeSharedUserLotId &&
            !hasUnits &&
            singleUnit &&
            isSharedUnitActive(singleUnit))
          || isDirectSharedLotActive)
          ? { boxShadow: `0 0 0 1px ${cardAccent}` }
          : {}),
        ...(!readOnly && isListLotViewActive
          ? { boxShadow: `0 0 0 1px ${cardAccent}` }
          : {}),
        ...(locateActive ? getRecordCardLocateActiveSx(cardAccent) : {}),
        ...(isMapHovered && !locateActive && !isListLotViewActive && !isSharedLotViewActive && !isDirectSharedLotActive
          ? { boxShadow: `0 0 0 2px ${cardAccent}` }
          : {}),
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: `${HEADER_GAP}px`,
          px: `${CARD_PAD_X}px`,
          pt: '10px',
          pb: '10px',
        }}
      >
        <RecordLotIdentity
          lot={lot}
          unitCount={hasUnits ? unitCount : 0}
          showMarker={isFavorite}
          markerColor={cardAccent}
          showLotContent={showContentChip}
          lotContentColor={cardAccent}
          lotContent={lotContent}
          locationAdornment={canExpand ? <RecordExpandButton expanded={isExpanded} /> : null}
          expanded={isExpanded}
          onLocationClick={canExpand
            ? () => onToggleExpand?.({ stopPropagation: () => {} }, lot.lot_id)
            : null}
          commercialProps={{
            primary: showCommercial || !showInquiryMeta ? '' : formatCollectionInquiryItemCountLabel(inquiryTotal),
            summary: lot.commercial_rollup,
            lotSummary: lot.commercial_summary || null,
            kind: 'rollup',
            // El bloque solo llega si se gestiona la lista (include pedido); en share falta → nada.
            showZero: true,
          }}
        />

        <Box onClick={(e) => e.stopPropagation()} sx={{ flexShrink: 0, height: 18, display: 'flex', alignItems: 'center' }}>
          <Tooltip title="Más acciones" placement="top" arrow>
            <IconButton
              size="small"
              onClick={openMenu}
              sx={{
                ...recordCardHeaderIconSx,
                color: TEXT_SECONDARY,
                '&:hover': { color: '#F9FAFB', bgcolor: 'rgba(255,255,255,0.06)' },
              }}
              aria-label="Más acciones"
            >
              <MoreVertIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={closeMenu}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={ACTION_MENU_PAPER_PROPS}
            MenuListProps={ACTION_MENU_LIST_PROPS}
          >
            <ActionMenuItem
              onClick={() => {
                closeMenu();
                onLocateLot?.(lot.lot_id, lot);
              }}
              icon={<MyLocationOutlinedIcon sx={{ fontSize: 20 }} />}
              label={isLocateActive ? 'Quitar marca del mapa' : 'Ubicar en el mapa'}
            />
            {(readOnly ? onOpenSharedLot : onOpenListLot) && (
              <ActionMenuItem
                onClick={() => {
                  closeMenu();
                  if (readOnly) onOpenSharedLot?.(lot);
                  else onOpenListLot?.(lot);
                }}
                icon={<VisibilityOutlinedIcon sx={{ fontSize: 20 }} />}
                label="Ver lote"
              />
            )}
            {canRemoveFromList && (
              <ActionMenuItem
                onClick={handleRemoveFromList}
                disabled={isRemovingFromList}
                loading={isRemovingFromList}
                icon={<DeleteOutlineIcon sx={{ fontSize: 20 }} />}
                label="Eliminar de la lista"
              />
            )}
          </Menu>
        </Box>
      </Box>

      {canExpand && isExpanded && (
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            bgcolor: BG_UNITS,
            borderTop: `1px solid ${BORDER}`,
          }}
        >
          {lotContent ? (
            <Box
              onClick={openLotContent ? (e) => { e.stopPropagation(); openLotContent(lot); } : undefined}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px',
                pl: `${contentPadLeft}px`,
                pr: `${CARD_PAD_X}px`,
                py: '10px',
                borderBottom: hasUnits ? `1px solid ${BORDER}` : 'none',
                ...(openLotContent ? { cursor: 'pointer' } : {}),
              }}
            >
              <RecordLotContentIdentity summary={lotContent} />
            </Box>
          ) : null}
          {units.map((unit, idx) => {
            const isLast = idx === units.length - 1;
            return (
              <Box
                key={unit.id || unit.user_lot_id}
                onClick={
                  (readOnly && onOpenSharedUnit) || onOpenListUnit
                    ? (e) => {
                        e.stopPropagation();
                        if (readOnly && onOpenSharedUnit) openSharedUnitDetail(unit);
                        else if (onOpenListUnit) onOpenListUnit(lot, unit);
                      }
                    : undefined
                }
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: '10px',
                  pl: `${contentPadLeft}px`,
                  pr: `${CARD_PAD_X}px`,
                  py: '10px',
                  borderBottom: isLast ? 'none' : `1px solid ${BORDER}`,
                  ...((readOnly && onOpenSharedUnit) || onOpenListUnit ? { cursor: 'pointer' } : {}),
                  ...(isSharedUnitActive(unit) ? { bgcolor: 'rgba(72,146,145,0.12)' } : {}),
                }}
              >
                <RecordUnitIdentity
                  unit={unit}
                  showZero
                  location={unitLocationDistinctFromLot(lot, unit)}
                />
                {!readOnly && onRemoveUnit && (
                  <Tooltip title="Eliminar de la lista" placement="top" arrow>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemoveUnit(unit);
                      }}
                      disabled={updatingUnitId === unit.id}
                      sx={{
                        ...recordCardHeaderIconSx,
                        color: TEXT_SECONDARY,
                        '&:hover': { color: '#ef4444', bgcolor: 'rgba(239,68,68,0.10)' },
                      }}
                    >
                      {updatingUnitId === unit.id ? (
                        <CircularProgress size={16} sx={{ color: '#9CA3AF' }} />
                      ) : (
                        <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                      )}
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
